import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import './AdminAiAgentLogs.css';
import { fmtDateTime } from '../../../utils/ruDates';

const PAGE_SIZE = 100;

const FILTERS = [
    { value: '', label: 'Все' },
    { value: 'active', label: 'Активные' },
    { value: 'expired', label: 'Истёкшие' },
    { value: 'revoked', label: 'Отозванные' },
];

const STATE_LABELS = { active: 'Активна', expired: 'Истекла', revoked: 'Отозвана' };
const STATE_BADGE = { active: 'completed', expired: 'interrupted', revoked: 'failed' };

function formatDate(iso) {
    if (!iso) return '—';
    return fmtDateTime(iso);
}

function AdminAiChatShares() {
    const { request } = useApi();
    const [state, setState] = useState('active');
    const [page, setPage] = useState(0);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
            if (state) params.set('state', state);
            const data = await request(`/api/v1/admin/ai-chat/shares?${params.toString()}`);
            setItems(data?.items || []);
            setTotal(data?.total || 0);
        } catch (error) {
            logger.error('Failed to load AI chat shares', error);
        } finally {
            setLoading(false);
        }
    }, [request, state, page]);

    useEffect(() => { load(); }, [load]);

    const revoke = async (id) => {
        if (!window.confirm('Отозвать ссылку? Чат по ней перестанет открываться.')) return;
        try {
            await request(`/api/v1/admin/ai-chat/shares/${id}/revoke`, { method: 'POST' });
            await load();
        } catch (error) {
            logger.error('Failed to revoke AI chat share', error);
        }
    };

    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="admin-page aal">
            <div className="aal-header">
                <div>
                    <h1>Публичные ссылки на чаты</h1>
                    <p className="aal-muted">Кто поделился AI-чатом по ссылке, сколько раз её открывали. Лишнее можно отозвать.</p>
                </div>
                <button type="button" className="aal-button" onClick={load} disabled={loading}>
                    {loading ? 'Загрузка…' : 'Обновить'}
                </button>
            </div>

            <div className="aal-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.value || 'all'}
                        type="button"
                        className={`aal-chip ${state === f.value ? 'aal-chip--active' : ''}`}
                        onClick={() => { setState(f.value); setPage(0); }}
                    >
                        {f.label}
                    </button>
                ))}
                <span className="aal-muted">Всего: {total}</span>
            </div>

            <div className="aal-list" style={{ maxHeight: 'none' }}>
                {items.length === 0 && !loading && <div className="aal-empty">Ссылок нет.</div>}
                {items.map((item) => (
                    <div key={item.id} className="aal-run" style={{ cursor: 'default' }}>
                        <div className="aal-run__top">
                            <span className={`aal-badge aal-badge--${STATE_BADGE[item.state] || 'pending'}`}>
                                {STATE_LABELS[item.state] || item.state}
                                {item.revoked_by_admin ? ' (админ)' : ''}
                            </span>
                            <span>{item.user_email}</span>
                            <span className="aal-run__duration">просмотров {item.view_count}</span>
                        </div>
                        <div className="aal-run__question">{item.session_title || 'Без названия'}</div>
                        <div className="aal-run__bottom">
                            <span>создана {formatDate(item.created_at)}</span>
                            <span>{item.expires_at ? `до ${formatDate(item.expires_at)}` : 'без срока'}</span>
                            {item.last_viewed_at && <span>последний просмотр {formatDate(item.last_viewed_at)}</span>}
                            {item.state === 'active' && (
                                <button type="button" className="aal-button" style={{ marginLeft: 'auto' }} onClick={() => revoke(item.id)}>
                                    Отозвать
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {pages > 1 && (
                    <div className="aal-pager">
                        <button type="button" className="aal-button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>←</button>
                        <span>{page + 1} / {pages}</span>
                        <button type="button" className="aal-button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>→</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminAiChatShares;
